import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Account } from '@feathr/blackbox';
import { EPackages, EPackageTiers } from '@feathr/blackbox';
import type { ISelectGroupOptions } from '@feathr/components';
import { Checkbox, DebouncedInput, Input, Select, TanStackTable } from '@feathr/components';
import UserSelect from '@feathr/extender/components/UserSelect';
import type { TCollectionTableSort } from '@feathr/extender/hooks';
import { useCollectionTable } from '@feathr/extender/hooks';
import { useStore, useUser } from '@feathr/extender/state';
import { packageTiersToLabelMap } from '@feathr/extender/styles/license';

import Page from '../Page/Page';
import accountsColumns from './accountsColumns';

import * as styles from './AccountsPage.css';

/** Accounts are listed alphabetically by name until the user sorts on another column. */
const INITIAL_SORT: TCollectionTableSort = [{ id: 'name' }];
/*
 * Must be one of the sizes the pagination dropdown offers (10/20/50/100) — it selects the current
 * value by matching it against that list, so any other number leaves the dropdown blank.
 */
const INITIAL_PAGE_SIZE = 20;

interface IFilters {
  csm?: string;
  name__icontains?: string;
  id__in?: string[];
  is_archived?: boolean;
  is_archived__ne?: boolean;
  license__packages__name__in?: EPackages[];
}

function AccountsPage(): JSX.Element {
  const { Accounts, Users } = useStore();
  const user = useUser();
  const [filters, setFilters] = useState<IFilters>({
    is_archived__ne: true,
  });
  const { t } = useTranslation();

  const favorites = user.getSetting('favorite_account_ids') || observable([] as string[]);

  function handleFavoritesChange(newValue?: boolean): void {
    if (newValue) {
      setFilters({
        ...filters,
        id__in: favorites,
      });
    } else {
      setFilters({
        ...filters,
        id__in: undefined,
      });
    }
  }

  function handleArchivedChange(newValue?: boolean): void {
    if (newValue) {
      setFilters({
        ...filters,
        is_archived: true,
        is_archived__ne: undefined,
      });
    } else {
      setFilters({
        ...filters,
        is_archived: undefined,
        is_archived__ne: true,
      });
    }
  }

  function handleAccountManagerChange(value?: string): void {
    setFilters({ ...filters, csm: value });
  }

  function handleAccountManagerClear(): void {
    setFilters({ ...filters, csm: undefined });
  }

  interface ILicensePackageOption {
    id: EPackages;
    name: string;
  }

  interface ILicensePackageGroupOptions extends ISelectGroupOptions {
    options: ILicensePackageOption[];
  }

  const tierOptions: ILicensePackageGroupOptions[] = useMemo(
    () => [
      {
        label: t('Platform Packages'),
        options: [
          {
            id: EPackages.FeathrPlatformLite,
            name: packageTiersToLabelMap(t, EPackageTiers.Lite),
          },
          {
            id: EPackages.FeathrPlatformBase,
            name: packageTiersToLabelMap(t, EPackageTiers.Base),
          },
          {
            id: EPackages.FeathrPlatformStarter,
            name: packageTiersToLabelMap(t, EPackageTiers.Starter),
          },
          {
            id: EPackages.FeathrPlatformEssential,
            name: packageTiersToLabelMap(t, EPackageTiers.Essential),
          },
          {
            id: EPackages.FeathrPlatformAdvanced,
            name: packageTiersToLabelMap(t, EPackageTiers.Advanced),
          },
          {
            id: EPackages.FeathrPlatformEnterprise,
            name: packageTiersToLabelMap(t, EPackageTiers.Enterprise),
          },
        ],
      },
      {
        label: t('Legacy Packages'),
        options: [
          { id: EPackages.Finch, name: packageTiersToLabelMap(t, EPackageTiers.Finch) },
          { id: EPackages.Falcon, name: packageTiersToLabelMap(t, EPackageTiers.Falcon) },
          {
            id: EPackages.EnterpriseFalcon,
            name: packageTiersToLabelMap(t, EPackageTiers.EnterpriseFalcon),
          },
          { id: EPackages.Phoenix, name: packageTiersToLabelMap(t, EPackageTiers.Phoenix) },
        ],
      },
    ],
    [t],
  );

  function handleTierChange(option: ILicensePackageOption): void {
    function parsEPackages(): EPackages[] | undefined {
      const licensePackage = option?.id;

      if (!licensePackage) {
        return undefined;
      }

      switch (licensePackage) {
        case EPackages.Falcon:
          return [
            EPackages.Falcon,
            EPackages.FalconLicenseExtended,
            EPackages.EnterpriseFalcon,
            EPackages.FalconLicense,
          ];

        case EPackages.EnterpriseFalcon:
          return [
            EPackages.EnterpriseFalcon,
            EPackages.EnterpriseFalconSeat,
            EPackages.EnterpriseFalconUnlimitedSeats,
            EPackages.EnterpriseFlamingo,
          ];

        case EPackages.Finch:
          return [
            EPackages.Finch,
            EPackages.FinchLicenseExtended,
            EPackages.FinchLegacy,
            EPackages.FinchLicense,
            EPackages.FinchLegacyLicense,
          ];

        case EPackages.Phoenix:
          return [EPackages.Phoenix, EPackages.PhoenixLicenseExtended, EPackages.PhoenixLicense];

        case EPackages.FeathrPlatformStarter:
          return [EPackages.FeathrPlatformStarter];

        case EPackages.FeathrPlatformEssential:
          return [EPackages.FeathrPlatformEssential, EPackages.FeathrPlatformEssentialPro];

        case EPackages.FeathrPlatformAdvanced:
          return [EPackages.FeathrPlatformAdvanced, EPackages.FeathrPlatformAdvancedPro];

        case EPackages.FeathrPlatformEnterprise:
          return [EPackages.FeathrPlatformEnterprise, EPackages.FeathrPlatformEnterprisePro];

        default:
          return [licensePackage];
      }
    }

    setFilters({
      ...filters,
      license__packages__name__in: parsEPackages(),
    });
  }

  function handleTierClear(): void {
    setFilters({
      ...filters,
      license__packages__name__in: undefined,
    });
  }

  function handleDebouncedSearchChange(newValue?: string): void {
    setFilters({
      ...filters,
      name__icontains: newValue ?? undefined,
    });
  }

  const filterElements = [
    <>
      <Checkbox
        className={styles.filterCheckbox}
        label={t('Favorites')}
        onChange={handleFavoritesChange}
        value={filters.id__in !== undefined}
        wrapperClassName={styles.filterCheckboxWrapper}
      />
      {user.isSudoer ? (
        <Checkbox
          className={styles.filterCheckbox}
          label={t('Archived')}
          onChange={handleArchivedChange}
          value={!filters.is_archived__ne}
          wrapperClassName={styles.filterCheckboxWrapper}
        />
      ) : undefined}
      <Select
        className={styles.filter}
        isClearable={true}
        name={'license-package-select'}
        onChange={handleTierChange}
        onClear={handleTierClear}
        options={tierOptions}
        placeholder={t('Select package...')}
      />
      <UserSelect
        filters={{ role: 'csm', is_archived__ne: true, skip_acl: true }}
        isClearable={true}
        onChange={handleAccountManagerChange}
        onClear={handleAccountManagerClear}
        placeholder={t('Select Account Manager...')}
        queryOptions={{ fetchOptions: { headers: Users.getHeaders(false) } }}
        value={filters.csm || undefined}
      />
    </>,
    <>
      <DebouncedInput<string>
        defaultValue={filters.name__icontains ?? ''}
        onChange={handleDebouncedSearchChange}
      >
        {(liveValue, onChangeLiveValue): JSX.Element => (
          <Input
            className={styles.filter}
            isClearable={true}
            onChange={onChangeLiveValue}
            placeholder={t('Search by account name...')}
            prefix={<FontAwesomeIcon icon={faSearch} />}
            type={'text'}
            value={liveValue}
          />
        )}
      </DebouncedInput>
    </>,
  ];

  const columns = useMemo(() => accountsColumns(t), [t]);
  const tableProps = useCollectionTable<Account>(Accounts, {
    defaultParams: { filters },
    initialPageSize: INITIAL_PAGE_SIZE,
    initialSort: INITIAL_SORT,
  });

  return (
    <Page title={t('Accounts')}>
      <TanStackTable<Account>
        {...tableProps}
        columns={columns}
        filterElements={filterElements}
        noDataText={t('No accounts found.')}
      />
    </Page>
  );
}

// `useCollectionTable` reads the list response's observables, so the table would stay stuck on its
// loading state without this.
export default observer(AccountsPage);
